import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { useInterviewResponses, useProfile, useAuth, useInterviewResponseAnswerScores } from "hooks";
import { FormattedMessage } from "react-intl";
import {
  BANNER_TYPE,
  INTERVIEW_STATUSES,
  PROFILE_PREFERENCES,
  RATING_CONTENT_TYPES,
  RATING_RESOURCE_TYPES,
} from "constants/index";
import { Grid } from "components/Containers";
import { MessagesNoResults } from "components/Messages";
import Card from "components/Card";
import { colors, fontSize } from "style";
import { Spinner, TextLoader } from "components/Loader";
import { shortenText } from "utils";
import { Banner } from "components/Banner";
import { CandidateInterview } from "components/Candidate";
import { AiRating, ProfileVideoInterviewContentSection } from "components/Profile";
import { FinishedStep, InterviewAnswerLocation } from "components/VideoPresentation";
import Tooltip from "components/Tooltip";

/**
 * ProfileVideoInterviewList
 *
 * @param {String}     profileId
 * @param {String}     missionRoleId
 * @param {Function}   selectedInterview
 * @param {Function}   setInterviews
 * @param {Boolean}    skip
 * @param {Boolean}    skipProfileQuery
 */
const ProfileVideoInterviewList = ({
  profileId,
  missionRoleId,
  selectedInterview,
  setInterviews = () => {},
  skip,
  skipProfileQuery,
}) => {
  const { isMember, isAdmin, isClient } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const { data, loading } = useInterviewResponses(
    {
      filters: { profileId, missionRoleId, status: INTERVIEW_STATUSES.completed },
    },
    skip
  );
  const { data: profileData, loading: profileDataLoading } = useProfile({ profileId, skip: skipProfileQuery });

  const canShowInterviews = skipProfileQuery ? true : profileData?.profile?.canShowInterviews;
  const interviewToDisplay = selectedInterview || data?.find((interview) => !interview.deletedAt);
  const answers = interviewToDisplay?.answers || [];

  const interviewResponseAnswerIds = answers?.map((answer) => answer.interviewResponseAnswerId) || [];
  const canFetchScores = interviewToDisplay && (isAdmin || isClient) && interviewResponseAnswerIds.length > 0;

  const { data: scoresData } = useInterviewResponseAnswerScores({
    interviewResponseAnswerIds,
    skip: !canFetchScores,
  });

  useEffect(() => {
    setIsLoading(loading || profileDataLoading);
  }, [loading, profileDataLoading]);

  useEffect(() => {
    setInterviews(data);
  }, [data]);

  const scoresByAnswerId = useMemo(() => {
    return (scoresData || []).reduce((acc, score) => {
      acc[score.interviewResponseAnswerId] = score;
      return acc;
    }, {});
  }, [scoresData]);

  if (isMember) {
    if (isLoading) {
      return <Spinner />;
    }

    if (interviewToDisplay && canShowInterviews) {
      return (
        <FinishedStep
          questions={interviewToDisplay?.interview?.questions || []}
          answers={interviewToDisplay?.answers || []}
        />
      );
    }
  }

  return (
    <>
      {isLoading && (
        <StyledGrid cols={1} rowGap="3rem">
          {[...Array(4).keys()].map((item) => (
            <Grid.col key={item}>
              <VideoCard loading={true} />
            </Grid.col>
          ))}
        </StyledGrid>
      )}
      {!isLoading &&
        canShowInterviews &&
        (interviewToDisplay ? (
          <StyledGrid cols={1} rowGap="3rem">
            {interviewToDisplay.answers?.map((answer, index) => (
              <Grid.col key={`${index}`}>
                <VideoCard
                  interviewId={interviewToDisplay?.uuid}
                  vectorData={scoresByAnswerId[answer.interviewResponseAnswerId]}
                  {...answer}
                />
              </Grid.col>
            ))}
          </StyledGrid>
        ) : (
          <>
            {isMember && data?.length === 0 ? (
              <Grid.col start={1} end={13}>
                <Card>
                  <CandidateInterview profileId={profileId} />
                </Card>
              </Grid.col>
            ) : (
              <Card noPadding>
                <MessagesNoResults />
              </Card>
            )}
          </>
        ))}
      {!isLoading && !canShowInterviews && (
        <Card>
          <Banner type={BANNER_TYPE.warning} isClosable={false}>
            <FormattedMessage
              id={`Profile.ProfilePreferences.${PROFILE_PREFERENCES.showVideoInterview}.NotAvailable`}
            />
          </Banner>
        </Card>
      )}
    </>
  );
};

/**
 * VideoCard
 *
 * @param {String}  title
 * @param {String}  description
 * @param {String}  videoUrl
 * @param {String}  thumbnailUrl
 * @param {String}  transcript
 * @param {Boolean} loading
 * @param {String}  interviewId
 * @param {String}  transcriptHash
 * @param {String}  transcriptSummary
 * @param {Object}  geoLocation
 * @param {Object}  vectorData
 */
const VideoCard = ({
  interviewId,
  title,
  description,
  videoUrl,
  thumbnailUrl,
  transcript,
  transcriptHash,
  transcriptSummary,
  loading,
  geoLocation,
  vectorData,
}) => {
  const [showMore, setShowMore] = useState(false);
  const videoRef = useRef(null);
  const { vectorScore, vectorRank } = vectorData || {};

  const scoreTier = vectorScore != null ? getScoreTier(vectorScore) : null;
  const scorePercent = vectorScore != null ? Math.round(vectorScore * 100) : null;

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [videoUrl]);

  return (
    <StyledCard>
      <div css={styles.header}>
        <div css={styles.title}>{loading ? <TextLoader width="60%" height="1rem" /> : title}</div>

        {!loading && vectorScore != null && (
          <Tooltip
            id={`vector-score-tooltip-${interviewId}`}
            tooltipId={`Profile.VideoInterviews.VectorScore.Tooltip${vectorRank ? "" : "NoRank"}`}
            values={{ percentage: scorePercent, rank: vectorRank }}
          >
            <div css={styles.vectorBadge(scoreTier)}>
              <span css={styles.vectorDot(scoreTier)} />
              <span>{scoreTier.label}</span>
            </div>
          </Tooltip>
        )}
      </div>
      <div css={styles.content_container}>
        <div css={styles.video_section}>
          <div css={styles.video_container(loading)}>
            {!loading && (
              <Video ref={videoRef} controls={true} poster={thumbnailUrl}>
                <source type="video/mp4" src={videoUrl} />
              </Video>
            )}
          </div>
          <div css={styles.description} onClick={() => setShowMore(!showMore)}>
            {loading ? (
              <>
                <TextLoader width="100%" height="1rem" />
                <TextLoader width="100%" height="1rem" />
                <TextLoader width="60%" height="1rem" />
              </>
            ) : showMore ? (
              description
            ) : (
              shortenText(description, 180)
            )}
          </div>
          <InterviewAnswerLocation {...geoLocation} />
        </div>
        <div css={styles.transcript_container}>
          {(loading || transcriptSummary) && (
            <div css={styles.transcript_section}>
              <ProfileVideoInterviewContentSection
                loading={loading}
                title={<FormattedMessage id="Profile.VideoInterviews.Summary" />}
                content={transcriptSummary}
                emptyMessageId="Profile.VideoInterviews.EmptyTitle"
              />
            </div>
          )}
          <div css={styles.transcript_section}>
            <ProfileVideoInterviewContentSection
              loading={loading}
              title={<FormattedMessage id="Profile.VideoInterviews.Transcript" />}
              content={transcript}
              emptyMessageId="Profile.VideoInterviews.EmptyTitle"
              shortenContent={true}
            />
          </div>
          {transcript && (
            <AiRating
              resourceType={RATING_RESOURCE_TYPES.interviewResponse}
              resourceId={interviewId}
              contentType={RATING_CONTENT_TYPES.transcript}
              contentHash={transcriptHash}
              positionRight
            />
          )}
        </div>
      </div>
    </StyledCard>
  );
};

const getScoreTier = (score) => {
  const percentage = Math.round(score * 100);
  if (percentage >= 80) {
    return {
      label: <FormattedMessage id="Profile.VideoInterviews.VectorScore.HighConfidence" />,
      bg: colors.green6,
      color: colors.green,
    };
  }
  if (percentage >= 65) {
    return {
      label: <FormattedMessage id="Profile.VideoInterviews.VectorScore.MediumConfidence" />,
      bg: colors.blueVelvetLight2,
      color: colors.blueVelvetBase,
    };
  }
  return {
    label: <FormattedMessage id="Profile.VideoInterviews.VectorScore.LowConfidence" />,
    bg: colors.grayAnatomyLight5,
    color: colors.grayAnatomyBase,
  };
};

const styles = {
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    padding: 0 3rem;
    margin-bottom: 1rem;
  `,
  title: css`
    flex: 1;
    min-width: 0;
    font-size: ${fontSize.normal};
    font-weight: 500;
  `,
  vectorBadge: (tier) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    background-color: ${tier.bg};
    color: ${tier.color};
    font-size: ${fontSize.small};
    font-weight: 600;
    white-space: nowrap;
  `,
  vectorDot: (tier) => css`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${tier.color};
  `,
  content_container: css`
    display: flex;
    gap: 2rem;
    padding: 0 3rem;
  `,
  video_section: css`
    flex: 1;
    min-width: 0;
    max-width: 50%;
  `,
  transcript_container: css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  `,
  transcript_section: css`
    background: ${colors.grayAnatomyLight5};
    border-radius: 4px;
    padding: 1.5rem;
  `,
  description: css`
    font-size: ${fontSize.small};
    font-weight: 500;
    color: ${colors.grayAnatomyBase};
    line-height: 1.2;
    cursor: pointer;
    margin-top: 1rem;
  `,
  video_container: (loading) => css`
    width: 100%;
    background: ${colors.grayAnatomyLight5};
    ${loading && `padding-top:75%;`}
    margin-bottom: 1rem;
  `,
};

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledGrid = styled(Grid)``;

const StyledCard = styled(Card)`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 2rem 0;
`;

VideoCard.propTypes = {
  title: PropTypes.string,
  videoUrl: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  description: PropTypes.string,
  transcript: PropTypes.string,
  transcriptSummary: PropTypes.string,
  loading: PropTypes.bool,
  interviewId: PropTypes.string,
  transcriptHash: PropTypes.string,
  geoLocation: PropTypes.object,
  vectorData: PropTypes.object,
};

ProfileVideoInterviewList.propTypes = {
  profileId: PropTypes.string,
  missionRoleId: PropTypes.string,
  setInterviews: PropTypes.func,
  selectedInterview: PropTypes.object,
  skip: PropTypes.bool,
  skipProfileQuery: PropTypes.bool,
};

export default ProfileVideoInterviewList;
